/**
 * Decorative, cookie-free backdrop shown behind the blocking consent modal
 * (RQ10409305 / RQ10773473).
 *
 * The real app is NOT mounted before acknowledgment — that is what guarantees no
 * first-party cookie (incl. the GCP LB `DREAMWRIGHT_PROJECT` HTTP_COOKIE affinity
 * cookie, which the LB would set on ANY pre-consent API call), no cookie-like
 * storage, and no API request before the user chooses. To still give the
 * designer's "translucent layer of website content" look, this is a STATIC,
 * INERT copy of the landing (NavbarV2 + HeroSectionV2) — its markup/classes are
 * mirrored, not imported.
 *
 * The nav shell geometry below (px-4 sm:px-6 md:px-[84px] / the h-16 md:h-20
 * max-w-[1273px] row / gap-3) is hand-copied to match the shared
 * `layout/headerGeometry` SSOT. It is NOT imported from there — this component
 * must stay within the React + static-asset boundary (RFC-0782 / RQ10773473). A
 * vitest guard (sharedHeader.test.tsx) asserts these inlined values still equal
 * the SSOT constants so the mirror can't silently drift.
 *
 * ⚠️ KEEP IN SYNC WITH: `landing-v2/NavbarV2.tsx` and `landing-v2/HeroSectionV2.tsx`.
 * When the landing nav/hero layout, copy, or branding changes, update this copy
 * to match. (Also noted in CLAUDE.md → UI/UX Guidelines → "Cookie consent
 * backdrop".) It is intentionally blurred/dimmed behind the modal, so it need
 * only approximate the landing — but brand/logo/headline drift is visible.
 *
 * STRICT CONSTRAINT: import ONLY React (+ `cn`) and side-effect-free constants.
 * Do NOT import landing components, providers, hooks, auth/config utilities, or
 * analytics — nothing that fetches the API, writes storage, or sets a cookie.
 * The only static same-origin asset used is the logo icon: it loads from the
 * console origin (already serving this page), not the affinity-cookie API
 * backend, and sets no cookies / no storage. All controls are non-interactive
 * placeholders.
 *
 * The hero panel is a flat dark surface (NOT the landing `hero-bg.jpg` still).
 * The live landing hero plays a looping showreel video that this cookie/
 * network-free gate cannot load; the static frame previously used here read as a
 * stray placeholder image behind the login modal. A solid dark panel loads
 * instantly and matches the dimmed/blurred backdrop treatment.
 */
import { useEffect, useRef } from 'react'
import { APP_NAME } from '../../constants/brand'
import { TEMPLATES_GALLERY_ENABLED } from '../../constants/featureFlags'

export default function ConsentBackdrop() {
  const ref = useRef<HTMLDivElement>(null)

  // Mark the whole subtree inert: out of the tab order + accessibility tree,
  // non-interactive. `inert` is not a JSX prop in React 18, so set the DOM
  // property directly. `aria-hidden` + `pointer-events-none` are belt-and-braces.
  useEffect(() => {
    if (ref.current) ref.current.inert = true
  }, [])

  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-testid="consent-backdrop"
      className="pointer-events-none fixed inset-0 z-0 select-none overflow-hidden bg-[#0d0d0d]"
    >
      {/* Navbar (mirrors NavbarV2, logged-out) */}
      <div className="absolute left-0 right-0 top-0 z-50 bg-transparent px-4 sm:px-6 md:px-[84px]">
        <div className="mx-auto mt-3 flex h-12 w-full max-w-[1273px] items-center justify-between rounded-full border border-white/15 bg-black/30 pl-4 pr-1 backdrop-blur-xl md:mt-4 md:h-14 md:pl-6 md:pr-1">
          <div className="flex min-w-0 items-center gap-2 no-underline md:gap-3">
            <img
              src="/landing-v2/logo-icon.png"
              alt=""
              className="w-[22px] h-[22px] object-contain"
            />
            <span className="truncate font-[Fustat] text-lg font-semibold leading-none text-white opacity-[0.92] md:text-2xl">
              {APP_NAME}
            </span>
          </div>
          {/* Mirrors NavbarV2's right cluster: Templates link + Sign In pill.
              Static <span> (no <Link>, no t()) per the RFC-0782 import ban. */}
          <div className="flex items-center gap-3">
            {TEMPLATES_GALLERY_ENABLED && (
              <span className="hidden text-sm font-medium text-white/80 sm:inline">Templates</span>
            )}
            <div className="rounded-[40px] bg-white px-4 py-2.5 text-sm font-semibold text-black md:px-6 md:py-3">
              Sign In
            </div>
          </div>
        </div>
      </div>

      {/* Hero (mirrors HeroSectionV2) */}
      <div className="relative">
        <div className="relative min-h-[680px] w-full overflow-hidden bg-[#141414] md:h-[851px]">
          {/* Solid dark backdrop instead of the static hero-bg.jpg still frame.
              The live landing hero plays a looping showreel video (HeroSectionV2),
              but this consent-gate backdrop is cookie/network-free and cannot load
              that signed video — so a static image here read as a stray "placeholder"
              behind the login modal. A flat dark panel loads instantly, matches the
              dimmed/blurred backdrop treatment, and carries no misleading still. */}
          <div className="absolute inset-0 h-full w-full bg-[#141414]" />

          {/* Top gradient overlay */}
          <div
            className="absolute top-0 left-0 right-0 h-[228px]"
            style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)',
            }}
          />

          {/* Headline */}
          <div className="absolute left-5 right-5 top-32 flex flex-col gap-3 md:left-1/2 md:right-auto md:top-[204px] md:w-[1153px] md:-translate-x-1/2 md:flex-row md:items-center md:justify-between">
            <h1 className="max-w-sm font-[Fustat] text-hero font-semibold leading-[1.1] text-white [text-shadow:0_2px_12px_rgba(0,0,0,0.3)] md:max-w-[756px] md:text-display-lg md:leading-[1.2]">
              Bring your idea to life
            </h1>
            <p className="max-w-sm font-[Fustat] text-base leading-[1.35] text-white [text-shadow:0_1px_8px_rgba(0,0,0,0.3)] md:max-w-[436px] md:text-xl md:font-medium md:leading-[1.2]">
              An AI Director that helps turn your story idea into cinematic video, scene by scene.
            </p>
          </div>

          {/* Prompt stack — mirrors HeroSectionV2: bottom-anchored flex wrapper holding
              the (static) data-privacy disclaimer banner above the prompt card. Static &
              non-interactive: no state, no handlers, no storage (RFC-0782 import ban). */}
          <div className="absolute bottom-6 left-4 right-4 flex flex-col gap-2 md:bottom-[110px] md:left-1/2 md:right-auto md:w-[720px] md:max-w-none md:-translate-x-1/2">
            {/* Data-privacy disclaimer banner (static mirror of the live hero) */}
            <div className="flex items-start gap-3 rounded-[20px] border-[1.5px] border-white/50 bg-[rgba(42,42,42,0.7)] px-4 py-3 shadow-[0_4px_4px_rgba(0,0,0,0.25)] backdrop-blur-[20px]">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mt-0.5 h-5 w-5 shrink-0 text-amber-400"
              >
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
              <span className="flex-1 font-sans text-sm leading-[1.4] text-white/90">
                Do not input personal data into your prompts (including images,
                videos, or other content depicting yourself or others unless
                you&apos;ve obtained consent)
              </span>
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-white/60">
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </span>
            </div>

            {/* Prompt card */}
            <div className="flex h-[300px] flex-col rounded-[20px] border-[1.5px] border-white/50 bg-[rgba(42,42,42,0.7)] p-4 shadow-[0_4px_4px_rgba(0,0,0,0.25)] backdrop-blur-[20px] md:relative md:block md:h-[170px] md:w-full md:max-w-none md:p-0">
            <div className="relative h-32 font-sans text-base leading-[1.6] text-white/40 md:absolute md:left-[18.5px] md:right-[18.5px] md:top-[14.5px] md:h-[70px]">
              Describe your story...
            </div>
            <div className="mt-2 self-end font-mono text-xs font-medium text-white/25 md:absolute md:right-[18.5px] md:top-[90px] md:mt-0">
              0 / 10000
            </div>
            <div className="mt-auto flex flex-col gap-3 md:absolute md:bottom-[20px] md:left-[18.5px] md:right-[18.5px] md:mt-0 md:h-[38px] md:flex-row md:items-center md:justify-between">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <div className="flex h-9 min-w-0 items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-3.5 md:h-[38px]">
                  <div className="h-4 w-4 rounded-sm bg-white/40" />
                  <span className="max-w-[172px] truncate font-[Fustat] text-xs font-medium text-white/70 md:text-sm">
                    Look · styles
                  </span>
                </div>
                <div className="flex h-9 items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-3.5 md:h-[38px]">
                  <div className="h-4 w-4 rounded-sm bg-white/40" />
                  <span className="font-[Fustat] text-xs font-medium text-white/70 md:text-sm">9:16</span>
                </div>
              </div>
              <div className="flex h-9 w-full items-center justify-center rounded-full bg-white px-5 font-sans text-sm font-semibold text-[#0d0d0d] shadow-[0_4px_6px_rgba(0,0,0,0.25)] md:h-[38px] md:w-auto md:text-base">
                Create →
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
